import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { Check } from "@phosphor-icons/react";
import { useId, useMemo, type ReactNode } from "react";
import styled from "@emotion/styled";
import { twRing } from "@/lib/emotionTw";

export type ColorSwatchValue = string | string[];

export type ColorSwatchGroupProps = Omit<RadioGroupPrimitive.RadioGroupProps, "defaultValue" | "disabled" | "onValueChange" | "value"> & {
  value: ColorSwatchValue;
  onValueChange: (value: ColorSwatchValue) => void;
  options: ColorSwatchValue[];
  label?: ReactNode;
  disabled?: boolean;
  getOptionLabel?: (option: ColorSwatchValue, index: number) => string;
  optionClassName?: string;
};

const serializeColorOption = (option: ColorSwatchValue) => JSON.stringify(option);

const defaultOptionLabel = (option: ColorSwatchValue, index: number) => {
  if (Array.isArray(option)) {
    return `Palette ${index + 1}: ${option.join(", ")}`;
  }

  return option;
};

/* Styles are the exact compiled output of the pre-migration Tailwind
 * utilities, emitted in the original class-list order. Verified against the
 * frozen baseline build — do not "clean up" values. */

const ColorSwatchGroupRoot = styled("div", {
  label: "AgenticColorSwatchGroup-root"
})({
  display: "grid",
  gap: ".5rem"
});

const ColorSwatchGroupLabel = styled("span", {
  label: "AgenticColorSwatchGroup-label",
  shouldForwardProp: (propName) => propName !== "ownerState"
})<{ ownerState: { disabled: boolean } }>(({ ownerState }) => [
  {
    fontSize: "12px",
    fontWeight: 600,
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  ownerState.disabled && {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  }
]);

const ColorSwatchGroupItems = styled(RadioGroupPrimitive.Root, {
  label: "AgenticColorSwatchGroup-items"
})({
  display: "flex",
  flexWrap: "wrap",
  gap: ".5rem"
});

const ColorSwatchGroupItem = styled(RadioGroupPrimitive.Item, {
  label: "AgenticColorSwatchGroup-item"
})({
  position: "relative",
  display: "flex",
  height: "2.25rem",
  width: "3rem",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-control)",
  borderWidth: "2px",
  "--tw-border-opacity": "1",
  borderColor: "hsl(var(--bfrs-color-form-control-border) / var(--tw-border-opacity, 1))",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-surface-card) / var(--tw-bg-opacity, 1))",
  padding: ".25rem",
  "--tw-shadow": "var(--bfrs-shadow-low)",
  "--tw-shadow-colored": "var(--bfrs-shadow-low)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)",
  outline: "2px solid transparent",
  outlineOffset: "2px",
  transitionProperty:
    "color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter",
  transitionTimingFunction: "cubic-bezier(.4,0,.2,1)",
  transitionDuration: ".15s",
  "&:hover": {
    borderColor: "hsl(var(--bfrs-color-primary) / .7)"
  },
  // ring-border-focus is a full-opacity ring color (--tw-ring-opacity: 1).
  "&:focus-visible": {
    ...twRing("2px", "hsl(var(--bfrs-color-border-focus) / var(--tw-ring-opacity, 1))"),
    "--tw-ring-opacity": "1"
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: ".5"
  },
  // Later in the compiled stylesheet than the :hover border rule, so checked
  // wins over hover — keep after "&:hover".
  '&[data-state="checked"]': {
    "--tw-border-opacity": "1",
    borderColor: "hsl(var(--bfrs-color-primary) / var(--tw-border-opacity, 1))",
    "--tw-shadow": "var(--bfrs-shadow-elevated)",
    "--tw-shadow-colored": "var(--bfrs-shadow-elevated)",
    boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
  }
});

const ColorSwatchGroupPreview = styled("span", {
  label: "AgenticColorSwatchGroup-preview"
})({
  display: "flex",
  width: "100%",
  height: "100%",
  overflow: "hidden",
  borderRadius: "inherit"
});

const ColorSwatchGroupPreviewSegment = styled("span", {
  label: "AgenticColorSwatchGroup-previewSegment"
})({
  minWidth: "0px",
  flex: "1 1 0%"
});

const ColorSwatchGroupIndicator = styled(RadioGroupPrimitive.Indicator, {
  label: "AgenticColorSwatchGroup-indicator"
})({
  position: "absolute",
  right: "-.25rem",
  top: "-.25rem",
  display: "flex",
  width: "1rem",
  height: "1rem",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "var(--bfrs-radius-pill)",
  "--tw-bg-opacity": "1",
  backgroundColor: "hsl(var(--bfrs-color-primary) / var(--tw-bg-opacity, 1))",
  "--tw-text-opacity": "1",
  color: "hsl(var(--bfrs-color-primary-contrast) / var(--tw-text-opacity, 1))",
  "--tw-shadow": "var(--bfrs-shadow-low)",
  "--tw-shadow-colored": "var(--bfrs-shadow-low)",
  boxShadow: "var(--tw-ring-offset-shadow, 0 0 #0000),var(--tw-ring-shadow, 0 0 #0000),var(--tw-shadow)"
});

function SwatchPreview({ option }: { option: ColorSwatchValue }) {
  const colors = Array.isArray(option) ? option : [option];

  return (
    <ColorSwatchGroupPreview>
      {colors.map((color, index) => (
        <ColorSwatchGroupPreviewSegment key={`${color}-${index}`} style={{ backgroundColor: color }} />
      ))}
    </ColorSwatchGroupPreview>
  );
}

export function ColorSwatchGroup({
  className,
  optionClassName,
  value,
  onValueChange,
  options,
  label,
  disabled,
  getOptionLabel,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  ...props
}: ColorSwatchGroupProps) {
  const generatedId = useId();
  const labelId = label ? `${generatedId}-label` : undefined;
  const optionMap = useMemo(() => new Map(options.map((option) => [serializeColorOption(option), option])), [options]);
  const serializedValue = serializeColorOption(value);

  return (
    <ColorSwatchGroupRoot className={className}>
      {label && (
        <ColorSwatchGroupLabel id={labelId} ownerState={{ disabled: Boolean(disabled) }}>
          {label}
        </ColorSwatchGroupLabel>
      )}
      <ColorSwatchGroupItems
        {...props}
        value={serializedValue}
        aria-label={ariaLabel ?? (label ? undefined : "Color options")}
        aria-labelledby={ariaLabelledBy ?? labelId}
        aria-disabled={disabled}
        onValueChange={(nextValue) => {
          const option = optionMap.get(nextValue);
          if (option) {
            onValueChange(option);
          }
        }}
      >
        {options.map((option, index) => {
          const optionValue = serializeColorOption(option);
          const optionLabel = getOptionLabel?.(option, index) ?? defaultOptionLabel(option, index);

          return (
            <ColorSwatchGroupItem
              key={optionValue}
              value={optionValue}
              disabled={disabled}
              aria-label={optionLabel}
              title={optionLabel}
              className={optionClassName}
            >
              <SwatchPreview option={option} />
              <ColorSwatchGroupIndicator>
                <Check size={10} weight="bold" />
              </ColorSwatchGroupIndicator>
            </ColorSwatchGroupItem>
          );
        })}
      </ColorSwatchGroupItems>
    </ColorSwatchGroupRoot>
  );
}

ColorSwatchGroup.displayName = "ColorSwatchGroup";
